import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { Trans, useLingui } from "@lingui/react/macro";
import { Heading } from "@medoma/components/Heading/Heading";
import { PlainButton } from "@medoma/components/Button/Button";
import { useDialog } from "@medoma/components/Dialog/useDialog";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import BinIcon from "@medoma/components/icons/BinIcon";
import type { operationalStatusSchema } from "@medoma/models/equipment";
import {
  equipmentAssetStatusDictionary,
  equipmentAssetStatusSchema,
} from "@medoma/models/equipment";
import { DescriptionList } from "@/components/DescriptionList/DescriptionList";
import { CloseButton } from "@/components/NavigationButtons/NavigationButtons";
import { categoriesQueryOptions, deleteAsset } from "@/api/Equipment";
import { deducedError, useErrorMessageAlert } from "@/Utils/ErrorUtils";
import { CommentRow } from "./CommentRow";
import { NameRow } from "./NameRow";
import { OperationalStatusRow } from "./OperationalStatusRow";
import styles from "./AssetDetails.module.scss";

type AssetDetailsContentProps = {
  id: string;
  name: string;
  categoryName: string;
  comment: string | null | undefined;
  operationalStatus: z.infer<typeof operationalStatusSchema>;
  allocationStatus: z.infer<typeof equipmentAssetStatusSchema>;
};

const AssetDetailsContent = ({
  id,
  name,
  categoryName,
  comment,
  operationalStatus,
  allocationStatus,
}: AssetDetailsContentProps) => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { confirm } = useDialog();
  const { displayErrorMessageAlert } = useErrorMessageAlert();

  const { mutate: deleteAssetMutation, isPending: isDeleting } = useMutation({
    mutationFn: () => deleteAsset(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      void navigate("..");
    },
    onError: (error) => {
      displayErrorMessageAlert(
        `${t`Kunde inte ta bort utrustning.`} ${deducedError(error)}`,
      );
    },
  });

  const confirmAndDelete = async () => {
    const assetName = name;
    const confirmed = await confirm({
      title: t`Ta bort utrustning`,
      message: t`${assetName} kommer att tas bort. Detta går inte att ångra.`,
      confirmLabel: t`Ta bort`,
    });
    if (confirmed) {
      deleteAssetMutation();
    }
  };

  return (
    <>
      <DescriptionList>
        <NameRow assetId={id} name={name} />
        <DescriptionList.Term>
          <Trans>Kategori</Trans>
        </DescriptionList.Term>
        <DescriptionList.Description>
          {categoryName}
        </DescriptionList.Description>
        <DescriptionList.Term>
          <Trans>Status</Trans>
        </DescriptionList.Term>
        <DescriptionList.Description>
          {t(equipmentAssetStatusDictionary[allocationStatus])}
        </DescriptionList.Description>
        <CommentRow assetId={id} comment={comment} />
        <OperationalStatusRow
          assetId={id}
          operationalStatus={operationalStatus}
          isAllocated={
            allocationStatus === equipmentAssetStatusSchema.enum.allocated
          }
        />
      </DescriptionList>

      <div className={styles.deleteAction}>
        <PlainButton
          type="button"
          onClick={confirmAndDelete}
          disabled={isDeleting}
        >
          <BinIcon />
          <Trans>Ta bort</Trans>
        </PlainButton>
      </div>
    </>
  );
};

export const AssetDetails = () => {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { id } = z.object({ id: z.string() }).parse(useParams());

  const {
    data: categories,
    isPending,
    isError,
    error,
  } = useQuery(categoriesQueryOptions());

  if (isPending) {
    return <Loading message={t`Hämtar utrustning`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta utrustning.`} ${deducedError(error)}`}
      />
    );
  }

  const asset = categories.flatMap((c) => c.assets).find((a) => a.id === id);
  const category = categories.find((c) => c.id === asset?.categoryId);

  if (!asset || !category) {
    return <ErrorMessage message={t`Utrustningen hittades inte.`} />;
  }

  return (
    <article>
      <header className={styles.header}>
        <Heading level="h1">{asset.name}</Heading>
        <CloseButton close={() => navigate("..")} />
      </header>
      <AssetDetailsContent
        key={id}
        id={id}
        name={asset.name}
        categoryName={category.name}
        comment={asset.comment}
        operationalStatus={asset.operationalStatus}
        allocationStatus={asset.status}
      />
    </article>
  );
};
